'use client';

import { useState, useEffect, useCallback } from 'react';
import { Search, SlidersHorizontal, X, Package as PackageIcon, ArrowUpDown } from 'lucide-react';
import { trackViewItemList } from '@/lib/analytics/ga4';
import { TrustBar, WholeHouseDiscountBanner } from '@/components/ecommerce';
import { useActivePromo } from '@/lib/hooks/useActivePromo';
import ProductCard, { displayPrice } from '@/components/products/ProductCard';

// Maps package id (from /packages page) → category slugs to show.
// Categories not in the list are hidden when a package filter is active.
import { PILLARS, matchesPillar, type PillarKey } from '@/lib/catalog/pillars';
import PillarExplainer from '@/components/products/PillarExplainer';

const PACKAGE_FILTERS: Record<string, { name: string; categorySlugs: string[]; matchKeywords?: string[] }> = {
  essential: {
    name: 'Comfort Starter',
    categorySlugs: ['faux-wood-blinds', 'cellular-shades', 'roller-shades', 'wood-blinds', 'cordless-cellular-shades'],
  },
  'smart-home': {
    name: 'Everyday Smart Living',
    categorySlugs: ['cellular-shades', 'roller-shades', 'roman-shades', 'zebra-blinds'],
    matchKeywords: ['motor', 'smart', 'cordless'],
  },
  signature: {
    name: 'Luxury Smart Home',
    categorySlugs: ['cellular-shades', 'roller-shades', 'roman-shades', 'plantation-shutters', 'wood-blinds'],
    matchKeywords: ['motor', 'smart', 'premium', 'designer'],
  },
};

interface Category {
  category_id: number;
  name: string;
  slug: string;
  description?: string;
  image_url?: string;
  product_count?: number;
}

interface Product {
  product_id: number;
  name: string;
  slug: string;
  category_id: number;
  category_name: string;
  category_slug: string;
  base_price: number;
  primary_image_url?: string;
  short_description?: string;
  vendor_name?: string;
  // Optional — passed through from API when available, used to surface
  // social-proof + curation on the card.
  is_featured?: boolean | number;
  avg_rating?: number;
  review_count?: number;
  /** Real purchasable minimum at the reference size (incl. freight). */
  starting_price?: number | null;
  /** The window size `starting_price` is quoted for, e.g. '24" x 36"'. */
  starting_price_basis?: string | null;
  /** Comma-separated; drives the Motorized pillar. */
  control_types?: string | null;
  /** Comma-separated; drives the Blackout / Light Filtering pillars. */
  tags?: string | null;
}

// A product with no real starting price shows "Contact us for a quote" rather
// than a number. It must not be silently removed by the price slider, which
// would otherwise test its stale base_price.
function isQuoteOnly(p: Product): boolean {
  return !p.starting_price_basis;
}

interface ProductsClientProps {
  initialProducts: Product[];
  initialCategories: Category[];
  initialCategorySlug?: string;
  /** Four-pillar filter from `?style=` — blackout / light-filtering / motorized. */
  initialStyle?: PillarKey;
  initialPackage?: string;
  initialWindows?: number;
}

export default function ProductsClient({ initialProducts, initialCategories, initialCategorySlug, initialStyle, initialPackage, initialWindows }: ProductsClientProps) {
  // The pillar narrows the catalogue first; the category filter below then lets
  // the shopper pick a TYPE (Honeycomb, Roller, Zebra …) within that pillar.
  const [activeStyle, setActiveStyle] = useState<PillarKey | null>(initialStyle ?? null);

  const [activePackage, setActivePackage] = useState<string | null>(initialPackage && PACKAGE_FILTERS[initialPackage] ? initialPackage : null);
  const packageFilter = activePackage ? PACKAGE_FILTERS[activePackage] : null;
  const resolveCategoryId = useCallback((slugOrId: string | undefined): number | null => {
    if (!slugOrId) return null;
    const numId = Number(slugOrId);
    if (!isNaN(numId) && initialCategories.some(c => c.category_id === numId)) return numId;
    return initialCategories.find(c => c.slug === slugOrId)?.category_id ?? null;
  }, [initialCategories]);

  const [selectedCategory, setSelectedCategory] = useState<number | null>(
    () => resolveCategoryId(initialCategorySlug)
  );

  /**
   * Re-sync the filters when the URL changes.
   *
   * These two filters seed from props via a `useState` initializer, which runs
   * ONCE. On a client-side navigation — clicking Blackout then Motorized in the
   * nav — React keeps the same component instance, so the initializer never
   * re-ran: the URL and the server-rendered heading changed while the chip and
   * the product list stayed on the first pillar. Only a hard reload was right.
   */
  useEffect(() => {
    setActiveStyle(initialStyle ?? null);
  }, [initialStyle]);

  useEffect(() => {
    setSelectedCategory(resolveCategoryId(initialCategorySlug));
  }, [initialCategorySlug, resolveCategoryId]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 1000]);
  const [sortBy, setSortBy] = useState<string>('featured');
  const promo = useActivePromo();

  const products = initialProducts;
  const categories = initialCategories;

  // GA4: track product list view on mount and when category filter changes
  useEffect(() => {
    if (products.length === 0) return;
    const listName = selectedCategory
      ? categories.find(c => c.category_id === selectedCategory)?.name || 'All Products'
      : 'All Products';
    const filteredForList = selectedCategory
      ? products.filter(p => p.category_id === selectedCategory)
      : products;
    trackViewItemList(listName, filteredForList);
  }, [selectedCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter products
  const filteredProducts = products.filter((product) => {
    if (activeStyle && !matchesPillar(activeStyle, product)) return false;
    if (selectedCategory && product.category_id !== selectedCategory) return false;

    // Package filter — restrict to categories tied to the selected package.
    // If matchKeywords is set, also require at least one keyword match in name/description.
    if (packageFilter) {
      if (!packageFilter.categorySlugs.includes(product.category_slug)) return false;
      if (packageFilter.matchKeywords && packageFilter.matchKeywords.length > 0) {
        const haystack = `${product.name} ${product.short_description || ''}`.toLowerCase();
        const hasKeyword = packageFilter.matchKeywords.some(kw => haystack.includes(kw));
        if (!hasKeyword) return false;
      }
    }

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesName = product.name.toLowerCase().includes(search);
      const matchesCategory = product.category_name?.toLowerCase().includes(search);
      const matchesDescription = product.short_description?.toLowerCase().includes(search);
      if (!matchesName && !matchesCategory && !matchesDescription) return false;
    }

    if (!isQuoteOnly(product)) {
      const shownPrice = displayPrice(product);
      if (shownPrice < priceRange[0] || shownPrice > priceRange[1]) return false;
    }

    return true;
  });

  // Fallback: if a package filter is active and matchKeywords yielded zero,
  // relax the keyword constraint and just show the package's category set.
  const finalProducts = (packageFilter && filteredProducts.length === 0)
    ? products.filter(p => packageFilter.categorySlugs.includes(p.category_slug))
    : filteredProducts;

  // Sorting — applied to a copy so the source order is preserved.
  const sortedProducts = [...finalProducts].sort((a, b) => {
    switch (sortBy) {
      case 'price_asc':
        return displayPrice(a) - displayPrice(b);
      case 'price_desc':
        return displayPrice(b) - displayPrice(a);
      case 'name_asc':
        return a.name.localeCompare(b.name);
      case 'rating':
        return (b.avg_rating ?? 0) - (a.avg_rating ?? 0);
      case 'newest':
        return (b.product_id ?? 0) - (a.product_id ?? 0);
      case 'featured':
      default:
        // Featured first, then keep the incoming order.
        return (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0);
    }
  });

  const getCategoryById = (categoryId: number) => {
    return categories.find(c => c.category_id === categoryId);
  };

  return (
    <>
      {/* Trust strip — reassurance at the top of the listing */}
      <TrustBar />

      {/* Volume-pricing call-out. Placed on the listing rather than a single
          product because that is where someone shopping for several windows
          actually is. No percentage is quoted — none is wired into checkout. */}
      <WholeHouseDiscountBanner />

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Mobile Filter Toggle */}
        <div className="lg:hidden mb-4">
          <button
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            className="flex items-center gap-2 w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-700 hover:bg-gray-50"
          >
            <SlidersHorizontal className="h-5 w-5" />
            <span className="font-medium">Filters</span>
            {selectedCategory && (
              <span className="ml-auto bg-sb-red text-white text-xs px-2 py-1 rounded-full">1</span>
            )}
          </button>
        </div>

        {/* Active pillar, shown as a clearable chip so it is obvious the
            catalogue is narrowed — and so the shopper can widen it again
            without editing the URL. The TYPE filters in the sidebar keep
            working inside the pillar. */}
        {activeStyle && (
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <span className="text-sm text-gray-600">Showing:</span>
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-red px-4 py-1.5 text-sm font-semibold text-white">
              {PILLARS[activeStyle].label}
              <button
                type="button"
                onClick={() => setActiveStyle(null)}
                aria-label={`Clear the ${PILLARS[activeStyle].label} filter`}
                className="rounded-full px-1 leading-none hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                &times;
              </button>
            </span>
            <span className="text-sm text-gray-500">Narrow by type on the left.</span>
          </div>
        )}

        {/* Buying guidance for the pillar the shopper arrived on. The nav
            points here rather than at the landing pages, so the explanation has
            to live here or it is unreachable. */}
        {activeStyle && <PillarExplainer pillar={activeStyle} />}

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Sidebar - Filters */}
          <aside className={`
            lg:w-64 lg:block
            ${showMobileFilters ? 'block' : 'hidden'}
            bg-white rounded-lg shadow-sm border border-gray-200 p-6
            lg:sticky lg:top-4 lg:self-start
          `}>
            {/* Mobile Close Button */}
            <div className="lg:hidden flex justify-between items-center mb-4 pb-4 border-b">
              <h2 className="text-lg font-bold text-heading-charcoal">Filters</h2>
              <button
                onClick={() => setShowMobileFilters(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Search */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Search Products
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input maxLength={80}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sb-red focus:border-transparent"
                />
              </div>
            </div>

            {/* Categories */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Categories</h3>
              <div className="space-y-2">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                    selectedCategory === null
                      ? 'bg-sb-red text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  All Products ({products.length})
                </button>
                {categories.map((category) => {
                  const count = products.filter(p => p.category_id === category.category_id).length;
                  return (
                    <button
                      key={category.category_id}
                      onClick={() => setSelectedCategory(category.category_id)}
                      className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                        selectedCategory === category.category_id
                          ? 'bg-sb-red text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-sm">{category.name}</span>
                        <span className="text-xs opacity-75">({count})</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Price Range */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Price Range</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-600">Min Price</label>
                  <input
                    type="number"
                    value={priceRange[0]}
                    onChange={(e) => setPriceRange([Number(e.target.value), priceRange[1]])}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-600">Max Price</label>
                  <input
                    type="number"
                    value={priceRange[1]}
                    onChange={(e) => setPriceRange([priceRange[0], Number(e.target.value)])}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Clear Filters */}
            {(selectedCategory || searchTerm || priceRange[0] > 0 || priceRange[1] < 1000) && (
              <button
                onClick={() => {
                  setSelectedCategory(null);
                  setSearchTerm('');
                  setPriceRange([0, 1000]);
                }}
                className="w-full px-4 py-2 text-sm text-sb-red border border-sb-red rounded-lg hover:bg-sb-red hover:text-white transition-colors"
              >
                Clear All Filters
              </button>
            )}
          </aside>

          {/* Right Content - Products Grid */}
          <section className="flex-1" aria-label="Products">
            {/* Package banner — visible only when arriving from /packages */}
            {packageFilter && (
              <div className="bg-primary-red mb-6 text-white rounded-xl p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4 shadow-lg">
                <div className="flex items-start gap-3">
                  <PackageIcon className="h-8 w-8 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs uppercase tracking-wider text-white/80 font-semibold">Curated for your package</p>
                    <h3 className="text-lg md:text-xl font-bold">{packageFilter.name}{initialWindows ? ` · ${initialWindows} windows` : ''}</h3>
                    <p className="text-sm text-white/90 mt-1">Showing only products that fit this package. Add what you like to your cart.</p>
                  </div>
                </div>
                <button
                  onClick={() => setActivePackage(null)}
                  className="text-sm font-semibold bg-white text-primary-red hover:bg-gray-100 px-4 py-2 rounded-lg transition-colors whitespace-nowrap self-start md:self-auto"
                >
                  Show All Products
                </button>
              </div>
            )}

            {/* Results Header */}
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-bold text-heading-charcoal">
                  {selectedCategory
                    ? getCategoryById(selectedCategory)?.name || 'Products'
                    : 'All Products'}
                </h2>
                <p className="text-sm text-sb-text-muted mt-1">
                  {finalProducts.length} {finalProducts.length === 1 ? 'product' : 'products'} found
                </p>
              </div>

              {/* Sort control */}
              {finalProducts.length > 0 && (
                <div className="flex items-center gap-2 shrink-0">
                  <ArrowUpDown className="h-4 w-4 text-sb-text-muted hidden sm:block" aria-hidden />
                  <label htmlFor="sort-products" className="sr-only">Sort products</label>
                  <select
                    id="sort-products"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-sb-gold cursor-pointer"
                  >
                    <option value="featured">Featured</option>
                    <option value="price_asc">Price: Low to High</option>
                    <option value="price_desc">Price: High to Low</option>
                    <option value="rating">Top Rated</option>
                    <option value="name_asc">Name: A to Z</option>
                    <option value="newest">Newest</option>
                  </select>
                </div>
              )}
            </div>

            {/* No Results */}
            {finalProducts.length === 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <div className="max-w-md mx-auto">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Search className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No products found</h3>
                  <p className="text-gray-600 mb-6">
                    Try adjusting your filters or search terms to find what you're looking for.
                  </p>
                  <button
                    onClick={() => {
                      setSelectedCategory(null);
                      setSearchTerm('');
                      setPriceRange([0, 1000]);
                    }}
                    className="px-6 py-2 bg-sb-red text-white rounded-lg hover:bg-sb-red/90 transition-colors"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            )}

            {/* Products Grid */}
            {finalProducts.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {sortedProducts.map((product) => (
                  <ProductCard key={product.product_id} product={product} promo={promo} />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </>
  );
}
